import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical, faList } from '@fortawesome/pro-solid-svg-icons';
import { faClone, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';

import type { PropertyTypeOption } from '../property_edit_form/hooks/use_property_type_options';

export type { PropertyTypeOption };

export const RENT_ROLL_DRAG_TYPE = 'rent-roll-row';
const MULTI_FAMILY_PROPERTY_TYPE_ID = 6;

type DragItem = {
  index: number;
  id: number | null;
};

export type RentRollEscalation = {
  date: string | null;
  pricePerSfPerYear: number | string | null;
};

export type CustomFieldValueType =
  | 'integer'
  | 'float'
  | 'string'
  | 'text'
  | 'enum_value';

export type CustomFieldSchema = {
  id: number;
  label: string;
  valueType: CustomFieldValueType;
  enumOptions: string[] | null;
  defaultValue: string | null;
};

export type RentRollCustomFieldValue = {
  id: number | null;
  customFieldSchemaId: number;
  value: string | null;
};

export type RentRollEntryData = {
  id: number | null;
  sortOrder: number | null;
  suite: string | null;
  propertyTypeId: number | null;
  propertyTypeLabel: string | null;
  bedrooms: number | string | null;
  bathrooms: number | string | null;
  tenantName: string | null;
  size: number | string | null;
  pricePerSfPerYear: number | string | null;
  rent: number | string | null;
  marketRent: number | string | null;
  annualRent: number | string | null;
  securityDeposit: number | string | null;
  leaseStart: string | null;
  leaseEnd: string | null;
  comments: string | null;
  recoveryType: string | null;
  escalations: RentRollEscalation[];
  customFieldValues: RentRollCustomFieldValue[];
  markedForDeletion?: boolean;
};

export type ScalarEntryField = Exclude<
  keyof RentRollEntryData,
  'escalations' | 'customFieldValues' | 'markedForDeletion'
>;

export type RentRollEntryProps = {
  buildingSize: number | null;
  currencyLabel: string;
  customFieldSchemas: CustomFieldSchema[];
  entry: RentRollEntryData;
  hiddenColumns: Set<string>;
  index: number;
  measurementAreaLabel: string;
  propertyTypeOptions: PropertyTypeOption[];
  useMonthly: boolean;
  onChange: (entry: RentRollEntryData) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onReorder: (dragIndex: number, hoverIndex: number) => void;
};

const inputValue = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const monthlyFromAnnual = (annual: number | string | null): string => {
  if (annual === null || annual === undefined || annual === '') return '';
  const num = Number(annual);
  if (!Number.isFinite(num)) return '';
  return String(num / 12);
};

const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatPercentOfBuilding = (size: number | string | null, buildingSize: number | null): string => {
  const sizeNum = toNumber(size);
  if (sizeNum === null || buildingSize === null || buildingSize <= 0) return '';
  return ((100 * sizeNum) / buildingSize).toFixed(2);
};

const formatRentPerSf = (rent: number | string | null, size: number | string | null): string => {
  const rentNum = toNumber(rent);
  const sizeNum = toNumber(size);
  if (rentNum === null || sizeNum === null || sizeNum <= 0) return '';
  return String(rentNum / sizeNum);
};

const fillMissingRentFields = (entry: RentRollEntryData): RentRollEntryData => {
  const size = toNumber(entry.size);
  const pricePerSf = toNumber(entry.pricePerSfPerYear);
  const annualRent = toNumber(entry.annualRent);
  const filled = [size, pricePerSf, annualRent].filter(value => value !== null && value !== 0).length;
  if (filled !== 2) return entry;
  if (size === null && pricePerSf !== null && annualRent !== null) {
    return { ...entry, size: annualRent / pricePerSf };
  }
  if (pricePerSf === null && size !== null && annualRent !== null) {
    return { ...entry, pricePerSfPerYear: annualRent / size };
  }
  if (annualRent === null && size !== null && pricePerSf !== null) {
    return { ...entry, annualRent: size * pricePerSf };
  }
  return entry;
};

type DraftInputProps = {
  disabled?: boolean;
  value: string;
  onCommit: (raw: string) => void;
};

const DraftInput = ({ disabled, value, onCommit }: DraftInputProps) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <input
      className="form-control border-0 rent-roll-input"
      disabled={disabled}
      type="number"
      value={draft}
      onBlur={(event) => {
        onCommit(event.target.value);
      }}
      onChange={event => setDraft(event.target.value)}
    />
  );
};

const RentRollEntry = ({
  buildingSize,
  currencyLabel,
  customFieldSchemas,
  entry,
  hiddenColumns,
  index,
  measurementAreaLabel,
  propertyTypeOptions,
  useMonthly,
  onChange,
  onDelete,
  onDuplicate,
  onReorder,
}: RentRollEntryProps) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const handleRef = useRef<HTMLTableCellElement>(null);
  const [expanded, setExpanded] = useState(false);

  const updateEscalation = (escIndex: number, changes: Partial<RentRollEscalation>) => {
    onChange({
      ...entry,
      escalations: entry.escalations.map((esc, i) => (i === escIndex ? { ...esc, ...changes } : esc)),
    });
  };

  const addEscalation = () => {
    onChange({
      ...entry,
      escalations: [...entry.escalations, { date: null, pricePerSfPerYear: null }],
    });
  };

  const removeEscalation = (escIndex: number) => {
    onChange({
      ...entry,
      escalations: entry.escalations.filter((_, i) => i !== escIndex),
    });
  };

  const [{ isDragging }, drag, preview] = useDrag({
    type: RENT_ROLL_DRAG_TYPE,
    item: (): DragItem => ({ index, id: entry.id }),
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop<DragItem, void, Record<string, never>>({
    accept: RENT_ROLL_DRAG_TYPE,
    hover(item) {
      if (item.index === index) return;
      onReorder(item.index, index);
      item.index = index;
    },
  });

  preview(drop(rowRef));
  drag(handleRef);

  const update = (field: ScalarEntryField) => (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...entry, [field]: event.target.value });
  };

  const renderInput = useCallback((field: ScalarEntryField, options: { number?: boolean, disabled?: boolean } = {}) => (
    <input
      className="form-control border-0 rent-roll-input"
      disabled={options.disabled}
      type={options.number ? 'number' : 'text'}
      value={inputValue(entry[field])}
      onChange={update(field)}
    />
  ), [entry]);

  const hasTypeSelector = propertyTypeOptions.length > 1;
  const disableMultiFamilyInputs = hasTypeSelector && entry.propertyTypeId !== MULTI_FAMILY_PROPERTY_TYPE_ID;
  const disableNonMultiFamilyInputs = hasTypeSelector && entry.propertyTypeId === MULTI_FAMILY_PROPERTY_TYPE_ID;

  const commitChange = (changes: Partial<RentRollEntryData>, runFill: boolean) => {
    const next = { ...entry, ...changes };
    onChange(runFill ? fillMissingRentFields(next) : next);
  };

  const commitSize = (raw: string) => {
    if (raw === '') {
      commitChange({ size: null }, false);
    } else {
      commitChange({ size: raw }, true);
    }
  };

  const commitAnnualValue = (raw: string, key: 'pricePerSfPerYear' | 'annualRent') => {
    if (raw === '') {
      commitChange({ [key]: null }, false);
      return;
    }

    if (useMonthly) {
      const num = Number(raw);
      commitChange({ [key]: Number.isFinite(num) ? num * 12 : raw }, true);
    } else {
      commitChange({ [key]: raw }, true);
    }
  };

  const annualRentDisplay = useMonthly ? monthlyFromAnnual(entry.annualRent) : inputValue(entry.annualRent);
  const pricePerSfPerYearDisplay =
    useMonthly ? monthlyFromAnnual(entry.pricePerSfPerYear) : inputValue(entry.pricePerSfPerYear);

  const updateCustomField = (schemaId: number, value: string) => {
    const existingIndex = entry.customFieldValues.findIndex(v => v.customFieldSchemaId === schemaId);
    let nextValues: RentRollCustomFieldValue[];
    if (existingIndex >= 0) {
      nextValues = entry.customFieldValues.map((v, i) => (i === existingIndex ? { ...v, value } : v));
    } else {
      nextValues = [...entry.customFieldValues, { id: null, customFieldSchemaId: schemaId, value }];
    }
    onChange({ ...entry, customFieldValues: nextValues });
  };

  const customFieldValueFor = (schemaId: number): string => {
    const found = entry.customFieldValues.find(v => v.customFieldSchemaId === schemaId);
    return found?.value ?? '';
  };

  const renderCustomFieldInput = (schema: CustomFieldSchema) => {
    const value = customFieldValueFor(schema.id);
    const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      updateCustomField(schema.id, event.target.value);
    };

    switch (schema.valueType) {
    case 'integer':
    case 'float':
      return (
        <input
          className="form-control border-0 rent-roll-input"
          step={schema.valueType === 'integer' ? 1 : 0.01}
          type="number"
          value={value}
          onChange={onChange}
        />
      );
    case 'string':
      return (
        <input
          className="form-control border-0 rent-roll-input"
          type="text"
          value={value}
          onChange={onChange}
        />
      );
    case 'text':
      return (
        <textarea
          className="form-control border-0 rent-roll-input"
          value={value}
          onChange={onChange}
        />
      );
    case 'enum_value':
      return (
        <select
          className="form-select border-0 rent-roll-input"
          value={value}
          onChange={onChange}
        >
          <option value="" />
          {(schema.enumOptions ?? []).map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
  };

  return (
    <>
      <tr ref={rowRef} style={{ opacity: isDragging ? 0.3 : 1 }}>
        <td className="text-center text-muted" ref={handleRef} style={{ cursor: 'grab' }}>
          <FontAwesomeIcon icon={faGripDotsVertical} />
        </td>
        <td
          className="text-center text-primary clickable"
          style={{ cursor: 'pointer' }}
          onClick={() => setExpanded(prev => !prev)}
        >
          <OverlayTrigger overlay={<Tooltip>Show Rent Escalations</Tooltip>}>
            <FontAwesomeIcon icon={faList} />
          </OverlayTrigger>
        </td>
        <td className="p-0" style={{ minWidth: '150px' }}>{renderInput('suite')}</td>
        {propertyTypeOptions.length > 1 && (
          <td style={{ minWidth: '120px' }}>
            <select
              className="form-select"
              value={entry.propertyTypeId ?? ''}
              onChange={event => onChange({
                ...entry,
                propertyTypeId: event.target.value === '' ? null : Number(event.target.value),
              })}
            >
              <option value="" />
              {propertyTypeOptions.map(option => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </td>
        )}
        {!hiddenColumns.has('bedrooms') && (
          <td className="p-0">{renderInput('bedrooms', { disabled: disableMultiFamilyInputs, number: true })}</td>
        )}
        {!hiddenColumns.has('bathrooms') && (
          <td className="p-0">{renderInput('bathrooms', { disabled: disableMultiFamilyInputs, number: true })}</td>
        )}
        {!hiddenColumns.has('tenantName') && (
          <td className="p-0">{renderInput('tenantName', { disabled: disableNonMultiFamilyInputs })}</td>
        )}
        <td className="p-0"><DraftInput value={inputValue(entry.size)} onCommit={commitSize} /></td>
        {!hiddenColumns.has('percentOfBuilding') && (
          <td>{formatPercentOfBuilding(entry.size, buildingSize)}</td>
        )}
        {!hiddenColumns.has('pricePerSfPerYear') && (
          <td className="p-0">
            <DraftInput
              disabled={disableNonMultiFamilyInputs}
              value={pricePerSfPerYearDisplay}
              onCommit={val => commitAnnualValue(val, 'pricePerSfPerYear')}
            />
          </td>
        )}
        {!hiddenColumns.has('rent') && (
          <td className="p-0">{renderInput('rent', { disabled: disableMultiFamilyInputs, number: true })}</td>
        )}
        <td className="p-0">{renderInput('marketRent', { number: true })}</td>
        {!hiddenColumns.has('rentPerSf') && <td>{formatRentPerSf(entry.rent, entry.size)}</td>}
        {!hiddenColumns.has('annualRent') && (
          <td className="p-0">
            <DraftInput
              disabled={disableNonMultiFamilyInputs}
              value={annualRentDisplay}
              onCommit={val => commitAnnualValue(val, 'annualRent')}
            />
          </td>
        )}
        {!hiddenColumns.has('securityDeposit') && (
          <td className="p-0">
            {renderInput('securityDeposit', { disabled: disableMultiFamilyInputs, number: true })}
          </td>
        )}
        <td className="p-0">{renderInput('leaseStart')}</td>
        <td className="p-0">{renderInput('leaseEnd')}</td>
        {customFieldSchemas.map(schema => (
          <td className="p-0" key={schema.id}>{renderCustomFieldInput(schema)}</td>
        ))}
        <td className="text-nowrap">
          <div className="d-flex flex-nowrap gap-2">
            <OverlayTrigger overlay={<Tooltip>Duplicate Row</Tooltip>}>
              <FontAwesomeIcon
                className="clickable text-muted"
                icon={faClone}
                onClick={onDuplicate}
              />
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Delete Row</Tooltip>}>
              <FontAwesomeIcon
                className="clickable text-destructive"
                icon={faTrashAlt}
                onClick={onDelete}
              />
            </OverlayTrigger>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="bg-light p-0" colSpan={100}>
            <div className="p-4">
              <div className="row">
                <div className="col-6">
                  <label className="form-label mb-1">Escalations</label>
                  {entry.escalations.length > 0 && (
                    <table className="table table-condensed table-sm bg-white table-bordered border mb-2">
                      <thead>
                        <tr>
                          <th className="bg-white small">Date</th>
                          <th className="bg-white small">
                            {currencyLabel}/{measurementAreaLabel}/yr
                          </th>
                          <th className="bg-white small" />
                        </tr>
                      </thead>
                      <tbody>
                        {entry.escalations.map((esc, escIndex) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={escIndex}>
                            <td>
                              <input
                                className="form-control border-0"
                                type="text"
                                value={esc.date ?? ''}
                                onChange={event => updateEscalation(escIndex, { date: event.target.value })}
                              />
                            </td>
                            <td>
                              <input
                                className="form-control border-0"
                                type="number"
                                value={inputValue(esc.pricePerSfPerYear)}
                                onChange={event => (
                                  updateEscalation(escIndex, { pricePerSfPerYear: event.target.value })
                                )}
                              />
                            </td>
                            <td className="text-center align-middle">
                              <FontAwesomeIcon
                                className="clickable text-destructive"
                                icon={faTrashAlt}
                                onClick={() => removeEscalation(escIndex)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div>
                    <a className="clickable text-primary" onClick={addEscalation}>+ Add Escalation</a>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group mb-3">
                    <label className="form-label">Comments</label>
                    <textarea
                      className="form-control"
                      value={entry.comments ?? ''}
                      onChange={event => onChange({ ...entry, comments: event.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Recovery Type</label>
                    <input
                      className="form-control"
                      type="text"
                      value={entry.recoveryType ?? ''}
                      onChange={event => onChange({ ...entry, recoveryType: event.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default RentRollEntry;
